import * as React from 'react';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { CheckBox } from 'sos-common/js/components/FormControls/CheckBox';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { SectionClass, SectionProps, SectionState, FieldName } from '../PublicForm.types';
import { MAX_LENGTH_NUM } from '../publicform.validation.service';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';

export interface IGranskningAvUtbildningSectionProps extends SectionProps {
  isAssessment: boolean;
}

export interface IGranskningAvUtbildningSectionState extends SectionState {
  denyContact: boolean;
  startArValue: string;
}

const granskningRequiredFields = [
  FieldName.GranskningTillatKontakt,
  FieldName.GranskningUtbildningEfterGymnasiet,
  FieldName.GranskningExamen,
  FieldName.GranskningUtbildningsplats,
  FieldName.GranskningArbetatEfterExamen,
  FieldName.GranskningStartAr,
  FieldName.UtbildningslandGranskning,
  FieldName.GranskningYrkesutbildningLangd,
  FieldName.GranskningExamensAr,
];

export default class GranskningAvUtbildningSection extends SectionClass<
  IGranskningAvUtbildningSectionProps,
  IGranskningAvUtbildningSectionState
> {
  private _mandatoryString: string;
  private readonly _examensArRef: React.RefObject<TextInput>;

  constructor(props: IGranskningAvUtbildningSectionProps) {
    super(props);
    this._mandatoryString = ' ' + props.Translate('mandatory');
    this._examensArRef = React.createRef();
    this.state = {
      denyContact: false,
      startArValue: '',
      errors: props.isAssessment ? this.getInitialErrorMap(granskningRequiredFields) : new Map(),
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as IGranskningAvUtbildningSectionProps;

  onCheckboxChange = (field: FormField<boolean>) => {
    this.onChangeField({ ...field, value: field.value || null });
  };

  onStartArChanged = (field: FormField<string>) => {
    this.setState({ startArValue: field.value ?? '' }, () => {
      const errors = new Map(this.state.errors);
      this.onChangeField(field, errors);
      const examensAr = this._examensArRef.current?.state;
      if (examensAr?.value) {
        this.onChangeField({ ...examensAr, referenceValue: field.value }, errors);
      }
    });
  };

  onTillatKontaktChanged = (field: FormField<string>) => {
    const errors = new Map(this.state.errors);
    const { Translate } = this.props;
    const denyContact = field.value === Translate('Nej');

    if (denyContact) {
      errors.set(FieldName.GranskningNejAnledning, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: this.props.Translate('error_mandatory'),
      });
    } else {
      errors.delete(FieldName.GranskningNejAnledning);
    }

    this.setState({ denyContact, errors }, () => {
      this.onChangeField(field);
    });
  };

  public render() {
    const { Translate, formLanguage, isInEditMode, openWebAnsokanEpiTexts, isAssessment } = this.props;
    const { denyContact } = this.state;

    return (
      <>
        <section aria-labelledby="diarienummer-heading">
          <EpiserverProperty propertyName="DiarienummerHeading" isInEditMode={isInEditMode}>
            <h2 id="diarienummer-heading">{openWebAnsokanEpiTexts.DiarienummerHeading}</h2>
          </EpiserverProperty>
          <div className="row m-b-2">
            <div
              className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.OmDuHaretEttTidigareArendeAngeDiariNummer, [
                FieldName.GranskningUtbildningEfterGymnasiet,
              ])}`}>
              <TextInput
                onChange={this.onChangeField}
                errorMessage={this.getErrorMessage(FieldName.OmDuHaretEttTidigareArendeAngeDiariNummer)}
                label={Translate(FieldName.OmDuHaretEttTidigareArendeAngeDiariNummer)}
                fieldName={FieldName.OmDuHaretEttTidigareArendeAngeDiariNummer}
                maxLength={MAX_LENGTH_NUM}
                showMaxLengthMessage={true}
                maxLengthMessageLanguage={formLanguage}
                isRequired={false}
              />
            </div>
          </div>
          <hr className="formdivider" />
        </section>

        <section aria-labelledby="utbildning-heading">
          <EpiserverProperty propertyName="UtbildningHeading" isInEditMode={isInEditMode}>
            <h2 id="utbildning-heading">{openWebAnsokanEpiTexts.UtbildningHeading}</h2>
          </EpiserverProperty>

          {isAssessment && (
            <RadioButtonGroup
              onChange={this.onChangeField}
              errorMessage={this.getErrorMessage(FieldName.GranskningUtbildningEfterGymnasiet)}
              fieldName={FieldName.GranskningUtbildningEfterGymnasiet}
              label={Translate(FieldName.GranskningUtbildningEfterGymnasiet) + this._mandatoryString}
              ariaLabel={FieldName.GranskningUtbildningEfterGymnasiet}
              isRequired={true}>
              <RadioButton value={Translate('Ja')} label={Translate('ja')} />
              <RadioButton value={Translate('Nej')} label={Translate('nej')} />
            </RadioButtonGroup>
          )}

          {isAssessment && (
            <div className="row m-b-2 m-t-2">
              <div
                className={`col-sm-6 align-items-end ${this.getErrorSiblingClass(FieldName.GranskningExamen, [
                  FieldName.GranskningUtbildningsplats,
                ])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.GranskningExamen)}
                  label={Translate(FieldName.GranskningExamen) + this._mandatoryString}
                  fieldName={FieldName.GranskningExamen}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                  isRequired={true}
                />
              </div>
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.GranskningUtbildningsplats, [
                  FieldName.GranskningExamen,
                ])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.GranskningUtbildningsplats)}
                  label={Translate(FieldName.GranskningUtbildningsplats) + this._mandatoryString}
                  fieldName={FieldName.GranskningUtbildningsplats}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                  isRequired={true}
                />
              </div>
            </div>
          )}

          <div className="row m-b-2">
            {isAssessment && (
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.GranskningYrkesutbildningLangd, [
                  FieldName.UtbildningslandGranskning,
                ])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.GranskningYrkesutbildningLangd)}
                  label={Translate(FieldName.GranskningYrkesutbildningLangd) + this._mandatoryString}
                  fieldName={FieldName.GranskningYrkesutbildningLangd}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                  isRequired={true}
                />
              </div>
            )}
            <div
              className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.UtbildningslandGranskning, [
                FieldName.GranskningYrkesutbildningLangd,
              ])}`}>
              <TextInput
                onChange={this.onChangeField}
                errorMessage={this.getErrorMessage(FieldName.UtbildningslandGranskning)}
                label={Translate(FieldName.UtbildningslandGranskning) + this._mandatoryString}
                fieldName={FieldName.UtbildningslandGranskning}
                autoComplete="country-name"
                maxLength={MAX_LENGTH_NUM}
                showMaxLengthMessage={true}
                maxLengthMessageLanguage={formLanguage}
                isRequired={true}
              />
            </div>
          </div>

          {isAssessment && (
            <>
              <div className="row m-b-2">
                <div
                  className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.GranskningStartAr, [
                    FieldName.GranskningExamensAr,
                  ])}`}>
                  <TextInput
                    onChange={this.onStartArChanged}
                    errorMessage={this.getErrorMessage(FieldName.GranskningStartAr)}
                    label={Translate(FieldName.GranskningStartAr) + this._mandatoryString}
                    fieldName={FieldName.GranskningStartAr}
                    maxLength={4}
                    isRequired={true}
                    maxLengthMessageLanguage={formLanguage}
                  />
                </div>
                <div
                  className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.GranskningExamensAr, [
                    FieldName.GranskningStartAr,
                  ])}`}>
                  <TextInput
                    ref={this._examensArRef}
                    onChange={this.onChangeField}
                    referenceValue={this.state.startArValue}
                    errorMessage={this.getErrorMessage(FieldName.GranskningExamensAr)}
                    label={Translate(FieldName.GranskningExamensAr) + this._mandatoryString}
                    fieldName={FieldName.GranskningExamensAr}
                    maxLength={4}
                    isRequired={true}
                    maxLengthMessageLanguage={formLanguage}
                  />
                </div>
              </div>

              <RadioButtonGroup
                onChange={this.onChangeField}
                errorMessage={this.getErrorMessage(FieldName.GranskningArbetatEfterExamen)}
                fieldName={FieldName.GranskningArbetatEfterExamen}
                label={Translate(FieldName.GranskningArbetatEfterExamen) + this._mandatoryString}
                ariaLabel={FieldName.GranskningArbetatEfterExamen}
                isRequired={true}>
                <RadioButton value={Translate('Ja')} label={Translate('ja')} />
                <RadioButton value={Translate('Nej')} label={Translate('nej')} />
              </RadioButtonGroup>

              <RadioButtonGroup
                onChange={this.onTillatKontaktChanged}
                errorMessage={this.getErrorMessage(FieldName.GranskningTillatKontakt)}
                isRequired={true}
                fieldName={FieldName.GranskningTillatKontakt}
                label={Translate(FieldName.GranskningTillatKontakt) + this._mandatoryString}
                ariaLabel={FieldName.GranskningTillatKontakt}>
                <RadioButton value={Translate('Ja')} label={Translate('ja')} />
                <RadioButton value={Translate('Nej')} label={Translate('nej')} />
              </RadioButtonGroup>

              {denyContact && (
                <div className="row m-b-2">
                  <div className="col-sm-12">
                    <TextInput
                      id={FieldName.GranskningNejAnledning}
                      onChange={this.onChangeField}
                      errorMessage={this.getErrorMessage(FieldName.GranskningNejAnledning)}
                      label={Translate(FieldName.GranskningNejAnledning) + this._mandatoryString}
                      isRequired={true}
                      fieldName={FieldName.GranskningNejAnledning}
                      maxLength={MAX_LENGTH_NUM}
                      showMaxLengthMessage={true}
                      maxLengthMessageLanguage={formLanguage}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <hr className="formdivider" />
        </section>
      </>
    );
  }
}
